import {
  allowCreatingEntities,
  allowDeletingEntities,
  allowReadingEntities,
  allowUpdatingAtoms,
  allowUpdatingEntities,
  DefaultAtoms,
  DefaultEntities,
  GenericPermission,
  grantFieldPermission,
  inviteUserPermission,
  makePermission,
  type PermissionsByRole,
  resendInviteCodePermission,
  sendPasswordResetEmailPermission,
  type ValidProjectSpecificPermissions,
} from "@atoms/atom-types";
import { Atoms } from "../enums/atoms";
import { Entities } from "../enums/entities";
import { CustomPermissions } from "../generated/custom-permissions";
import {
  afsImportApiPermission,
  triggerImportOrdersPermission,
} from "./provisionReducerPermissions";

const allEntities = [
  Entities.UserOption,
  Entities.Order,
  Entities.Position,
  DefaultEntities.Global,
  DefaultEntities.User,
  Entities.ImportProcess,
  Entities.Preset,
];

const entitiesWithoutOrdersAndPositions = [
  DefaultEntities.Global,
  DefaultEntities.User,
];

const orderAndPositionEntities = [Entities.Order, Entities.Position];
const presetEntities = [Entities.Preset];

/** Explicit atom reads — required for pricing UI (`canAccessPriceData`); entity-level `Global/read` alone must not grant access. */
const globalPricingAtomsRead = [
  makePermission({
    entityType: DefaultEntities.Global,
    atomType: Atoms.PriceData,
    permission: GenericPermission.Read,
  }),
  makePermission({
    entityType: DefaultEntities.Global,
    atomType: Atoms.PriceParameters,
    permission: GenericPermission.Read,
  }),
];

/**
 * Single source for backend and frontend `permissionsByRoles`
 * (must stay in sync with `getProvisionRolesFromTransaction` role keys).
 */
export const provisionPermissionsByRoles = {
  admin: [
    ...allowCreatingEntities(allEntities),
    ...allowReadingEntities(allEntities),
    ...allowUpdatingEntities(allEntities),
    ...allowDeletingEntities(allEntities),
    ...globalPricingAtomsRead,
    ...grantFieldPermission(
      DefaultEntities.User,
      DefaultAtoms.PersonalUserData,
      ["isAdmin", "state"],
      GenericPermission.Update,
    ),
    ...grantFieldPermission(
      Entities.Position,
      Atoms.PositionInfo,
      ["createdAt", "source"],
      GenericPermission.Create,
    ),
    CustomPermissions.UpdateProtectedUserFields,
    CustomPermissions.ManageTaggedPresets,
    inviteUserPermission,
    sendPasswordResetEmailPermission,
    resendInviteCodePermission,
    triggerImportOrdersPermission,
    afsImportApiPermission,
  ],
  user: [
    ...allowReadingEntities(entitiesWithoutOrdersAndPositions),
    ...grantFieldPermission(
      DefaultEntities.User,
      DefaultAtoms.PersonalUserData,
      ["firstName", "lastName", "displayName", "imageUrl", "email"],
      GenericPermission.Update,
    ),
  ],
  usermanager: [
    ...allowCreatingEntities([DefaultEntities.User]),
    ...allowReadingEntities([DefaultEntities.User, DefaultEntities.Global]),
    ...grantFieldPermission(
      DefaultEntities.User,
      DefaultAtoms.PersonalUserData,
      ["firstName", "lastName", "displayName", "imageUrl", "email", "state"],
      GenericPermission.Update,
    ),
    inviteUserPermission,
    sendPasswordResetEmailPermission,
    resendInviteCodePermission,
  ],
  pricecalculator: [
    ...globalPricingAtomsRead,
    ...allowUpdatingAtoms(DefaultEntities.Global, Atoms.PriceData),
    ...allowUpdatingAtoms(DefaultEntities.Global, Atoms.PriceParameters),
  ],
  designer: [
    ...allowCreatingEntities(orderAndPositionEntities),
    ...allowReadingEntities(orderAndPositionEntities),
    ...allowUpdatingEntities(orderAndPositionEntities),
    ...allowDeletingEntities(orderAndPositionEntities),
    ...grantFieldPermission(
      Entities.Position,
      Atoms.PositionInfo,
      ["createdAt", "source"],
      GenericPermission.Create,
    ),
    ...allowCreatingEntities(presetEntities),
    ...allowReadingEntities(presetEntities),
    ...allowUpdatingEntities(presetEntities),
    ...allowDeletingEntities(presetEntities),
    ...allowCreatingEntities([Entities.UserOption]),
    ...allowReadingEntities([Entities.UserOption]),
    ...allowUpdatingEntities([Entities.UserOption]),
    ...allowDeletingEntities([Entities.UserOption]),
    ...allowReadingEntities([Entities.ImportProcess]),
    triggerImportOrdersPermission,
    afsImportApiPermission,
  ],
  sunlabimporttemplates: [
    ...allowCreatingEntities(presetEntities),
    ...allowReadingEntities(presetEntities),
    ...allowUpdatingEntities(presetEntities),
    ...allowDeletingEntities(presetEntities),
    CustomPermissions.ManageTaggedPresets,
  ],
  ordermanager: [
    ...allowReadingEntities([Entities.ImportProcess]),
    triggerImportOrdersPermission,
    afsImportApiPermission,
  ],
} as PermissionsByRole<string, ValidProjectSpecificPermissions>;
